<template>
  <div class="ex-scroll-bar-shell">
    <div
      ref="scrollEl"
      class="ex-scroll-bar-shell__content"
      :data-testid="scrollContainerTestId || undefined"
      @scroll="syncScrollbar"
    >
      <slot />
    </div>
    <div
      v-show="showScrollbar"
      class="ex-scroll-bar"
      data-testid="ex-scroll-bar"
      aria-hidden="true"
    >
      <div class="ex-scroll-bar__track">
        <div class="ex-scroll-bar__thumb" :style="thumbStyle" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
  /** Applied to the scrollable content container (for tests and a11y hooks). */
  scrollContainerTestId: {
    type: String,
    default: "",
  },
  /** Minimum thumb height in pixels. */
  minThumbHeight: {
    type: Number,
    default: 24,
  },
});

const scrollEl = ref(null);
const showScrollbar = ref(false);
const thumbHeight = ref(24);
const thumbOffset = ref(0);

const thumbStyle = computed(() => ({
  height: `${thumbHeight.value}px`,
  transform: `translateY(${thumbOffset.value}px)`,
}));

const syncScrollbar = () => {
  const el = scrollEl.value;
  if (!el) {
    return;
  }

  const viewportHeight = el.clientHeight;
  const contentHeight = el.scrollHeight;

  showScrollbar.value = contentHeight > viewportHeight + 1;

  if (!showScrollbar.value) {
    return;
  }

  const trackPadding = 8;
  const trackHeight = Math.max(viewportHeight - trackPadding, 0);
  const computedThumbHeight = Math.max(
    (viewportHeight / contentHeight) * trackHeight,
    props.minThumbHeight
  );
  const maxThumbOffset = Math.max(trackHeight - computedThumbHeight, 0);
  const maxScrollTop = Math.max(contentHeight - viewportHeight, 0);
  const offset =
    maxScrollTop === 0 ? 0 : (el.scrollTop / maxScrollTop) * maxThumbOffset;

  thumbHeight.value = Math.min(computedThumbHeight, trackHeight);
  thumbOffset.value = offset;
};

const scrollToBottom = () => {
  const el = scrollEl.value;
  if (!el) {
    return;
  }
  el.scrollTop = el.scrollHeight;
  syncScrollbar();
};

let resizeObserver = null;

onMounted(async () => {
  await nextTick();
  syncScrollbar();

  if (typeof ResizeObserver === "undefined" || !scrollEl.value) {
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    syncScrollbar();
  });
  resizeObserver.observe(scrollEl.value);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

defineExpose({
  scrollToBottom,
  syncScrollbar,
  scrollEl,
});
</script>

<style scoped>
.ex-scroll-bar-shell {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

.ex-scroll-bar-shell__content {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ex-scroll-bar-shell__content::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.ex-scroll-bar {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  width: 16px;
  padding: 4px;
  box-sizing: border-box;
  pointer-events: none;
}

.ex-scroll-bar__track {
  position: relative;
  flex: 1;
  min-width: 0;
}

.ex-scroll-bar__thumb {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: #eaecf0;
  border-radius: 9999px;
}
</style>
